import { useParticipantOptions } from "@/hooks/useParticipantOptions";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface ParticipantOptionSelectorProps {
  leadId: string;
  selectedOptionId: string | null;
  onSelect: (optionId: string, quantity: number, label: string) => void;
  /** If true, auto-selects single option without showing dropdown */
  autoSelectSingle?: boolean;
  /** Render as compact chip bar instead of dropdown */
  variant?: "dropdown" | "chips";
  className?: string;
}

export function ParticipantOptionSelector({
  leadId,
  selectedOptionId,
  onSelect,
  autoSelectSingle = true,
  variant = "dropdown",
  className = "",
}: ParticipantOptionSelectorProps) {
  const { options, isLoading } = useParticipantOptions(leadId);

  // Auto-select if only one option
  if (autoSelectSingle && options.length === 1 && !selectedOptionId) {
    // Use setTimeout to avoid state update during render
    setTimeout(() => onSelect(options[0].id, options[0].quantity, options[0].label), 0);
  }

  if (isLoading) return null;

  // Don't render if only 1 option and autoSelect is on
  if (options.length <= 1 && autoSelectSingle) return null;

  if (variant === "chips") {
    return (
      <div className={`flex items-center gap-2 flex-wrap ${className}`}>
        <span className="text-sm text-muted-foreground flex items-center gap-1">
          <Users className="h-3.5 w-3.5" />
          Pax Option:
        </span>
        {options.map((opt) => (
          <Badge
            key={opt.id}
            variant={selectedOptionId === opt.id ? "default" : "outline"}
            className="cursor-pointer transition-colors"
            onClick={() => onSelect(opt.id, opt.quantity, opt.label)}
          >
            {opt.label}: {opt.quantity} pax
          </Badge>
        ))}
      </div>
    );
  }

  return (
    <div className={`space-y-1.5 ${className}`}>
      <label className="text-sm font-medium flex items-center gap-1.5">
        <Users className="h-3.5 w-3.5" />
        Generate quote for which option?
      </label>
      <Select
        value={selectedOptionId || ""}
        onValueChange={(val) => {
          const opt = options.find((o) => o.id === val);
          if (opt) onSelect(opt.id, opt.quantity, opt.label);
        }}
      >
        <SelectTrigger>
          <SelectValue placeholder="Select participant option..." />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt.id} value={opt.id}>
              {opt.label}: {opt.quantity} pax
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
